
import React, { useState, useEffect } from 'react';
import { db } from '../services/firebase';
import { collection, query, where, onSnapshot, setDoc, doc, limit, getDoc } from 'firebase/firestore';
import { UserRole } from '../types';

interface Props {
  user: any;
}

const AttendanceManagement: React.FC<Props> = ({ user }) => {
  const [classes, setClasses] = useState<any[]>([]);
  const [selectedClass, setSelectedClass] = useState<any>(null);
  const [classStudents, setClassStudents] = useState<any[]>([]);
  const [attendanceLogs, setAttendanceLogs] = useState<any[]>([]);
  const [markedToday, setMarkedToday] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userClass, setUserClass] = useState<any>(null);
  
  // Date selection for Staff/Admin
  const [viewDate, setViewDate] = useState(new Date().toISOString().split('T')[0]);
  const todayStr = new Date().toISOString().split('T')[0];

  // Helper to sort classes like 1st, 2nd, 10th naturally
  const naturalSortClasses = (classList: any[]) => {
    return [...classList].sort((a, b) => {
        const extractNum = (s: string) => {
            const match = s.match(/\d+/);
            return match ? parseInt(match[0], 10) : 0;
        };
        const numA = extractNum(a.name);
        const numB = extractNum(b.name);
        if (numA !== numB) return numA - numB;
        return a.name.localeCompare(b.name);
    });
  };

  useEffect(() => {
    // 1. Fetch class info for student view
    if (user.role === UserRole.STUDENT && user.classId) {
      getDoc(doc(db, 'classes', user.classId)).then(snap => {
        if (snap.exists()) setUserClass({ id: snap.id, ...snap.data() });
      });
    }

    // 2. Fetch all classes for Staff/Admin
    if (user.role !== UserRole.STUDENT) {
      const unsubscribe = onSnapshot(collection(db, 'classes'), (snap) => {
        const rawClasses = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setClasses(naturalSortClasses(rawClasses));
      });
      return () => unsubscribe();
    }
  }, [user]);

  useEffect(() => {
    // 3. Fetch attendance logs
    // Removing 'orderBy' from Firestore queries that use 'where' on different fields
    // to avoid index requirement errors.
    let attQ;
    if (user.role === UserRole.STUDENT) {
      attQ = query(collection(db, 'attendance'), where('studentId', '==', user.uid));
    } else if (selectedClass) {
      attQ = query(
        collection(db, 'attendance'), 
        where('classId', '==', selectedClass.id), 
        where('date', '==', viewDate)
      );
    } else {
      attQ = query(collection(db, 'attendance'), limit(150));
    }

    const unsubscribe = onSnapshot(attQ, (snap) => {
      const logs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      // Client-side Sorting
      logs.sort((a: any, b: any) => b.timestamp - a.timestamp);
      
      setAttendanceLogs(logs);
      if (user.role === UserRole.STUDENT) {
        setMarkedToday(logs.some((l: any) => l.date === todayStr));
      }
    });

    return () => unsubscribe();
  }, [user, selectedClass, viewDate]);

  useEffect(() => {
    // 4. Fetch students for selected class (Staff/Admin view)
    if (selectedClass) {
      const q = query(
        collection(db, 'users'), 
        where('classId', '==', selectedClass.id), 
        where('role', '==', UserRole.STUDENT)
      );
      const unsubscribe = onSnapshot(q, (snap) => {
        setClassStudents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      });
      return () => unsubscribe();
    }
  }, [selectedClass]);

  const handleCheckIn = async () => {
    if (!user.classId) return alert("Class not assigned.");
    setLoading(true);
    const compositeId = `${user.uid}_${user.classId}_${todayStr}`;
    try {
      await setDoc(doc(db, 'attendance', compositeId), {
        studentId: user.uid,
        studentName: user.displayName,
        studentPhoto: user.photoURL,
        classId: user.classId,
        className: userClass?.name || 'Assigned Class',
        date: todayStr,
        status: 'present',
        timestamp: Date.now()
      });
    } catch (err) {
      alert("Error marking attendance.");
    } finally {
      setLoading(false);
    }
  };

  if (user.role === UserRole.STUDENT) {
    return (
      <div className="space-y-8 animate-fadeIn">
        <section className="bg-white dark:bg-dark-card p-10 rounded-[3rem] border border-gray-100 dark:border-gray-800 shadow-xl relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-purple-500/5 rounded-full"></div>
          <div className="relative z-10 text-center md:text-left">
            <h2 className="text-3xl font-black dark:text-white mb-2">Daily Attendance</h2>
            <p className="text-gray-400 font-bold uppercase tracking-widest text-xs mb-8">Digital Check-In Portal</p>
            
            <div className="flex flex-col md:flex-row gap-8 items-center">
                <div className="flex-1 w-full bg-gray-50 dark:bg-gray-800/50 p-8 rounded-3xl border border-gray-100 dark:border-gray-800">
                    <p className="text-[10px] font-black text-purple-600 uppercase tracking-widest mb-2">My Current Room</p>
                    <h3 className="text-2xl font-black dark:text-white">{userClass?.name || 'Searching...'}</h3>
                    <p className="text-xs text-gray-400 mt-2 font-bold">Session 2026-2027 • Live</p>
                </div>
                
                <button 
                  onClick={handleCheckIn}
                  disabled={loading || markedToday || !user.classId}
                  className={`w-full md:w-auto px-16 py-6 rounded-3xl font-black text-lg text-white shadow-2xl transition-all active:scale-95 flex items-center justify-center gap-4 ${
                    markedToday 
                    ? 'bg-green-500 cursor-default shadow-green-200' 
                    : !user.classId ? 'bg-gray-300' : 'bg-purple-600 hover:bg-purple-700 shadow-purple-200 dark:shadow-none'
                  }`}
                >
                  {loading ? <i className="fas fa-spinner fa-spin"></i> : markedToday ? (
                    <><i className="fas fa-check-circle text-2xl"></i> Checked In</>
                  ) : (
                    <><i className="fas fa-fingerprint text-2xl"></i> Mark Presence</>
                  )}
                </button>
            </div>
          </div>
        </section>

        <section className="space-y-6">
            <div className="flex items-center gap-3 px-2">
                <i className="fas fa-history text-purple-500"></i>
                <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">Attendance History</h3>
            </div>
            <div className="grid gap-4">
                {attendanceLogs.map((a: any) => (
                    <div key={a.id} className="bg-white dark:bg-dark-card p-6 rounded-3xl border border-gray-100 dark:border-gray-800 flex justify-between items-center group hover:shadow-lg transition-all">
                        <div className="flex items-center gap-5">
                            <div className="w-14 h-14 rounded-2xl bg-green-50 dark:bg-green-900/10 flex items-center justify-center text-green-500">
                                <i className="fas fa-calendar-day text-2xl"></i>
                            </div>
                            <div>
                                <h4 className="font-black dark:text-white text-lg">{a.className}</h4>
                                <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">{a.date}</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <span className="px-5 py-2 bg-green-500 text-white text-[10px] font-black rounded-full uppercase tracking-widest shadow-lg shadow-green-100 dark:shadow-none">Present</span>
                            <p className="text-[9px] text-gray-400 mt-2 font-mono">{new Date(a.timestamp).toLocaleTimeString()}</p>
                        </div>
                    </div>
                ))}
            </div>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-3xl font-black dark:text-white tracking-tight">Attendance Register</h2>
          <p className="text-xs text-gray-400 font-black uppercase tracking-widest mt-1">
            {selectedClass ? `Viewing Room: ${selectedClass.name}` : 'Select a classroom to manage'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {selectedClass && (
              <>
                <input 
                  type="date" 
                  value={viewDate} 
                  onChange={(e) => setViewDate(e.target.value)}
                  className="bg-white dark:bg-dark-card border border-gray-100 dark:border-gray-800 p-3 rounded-xl text-xs font-bold dark:text-white shadow-sm outline-none focus:ring-2 focus:ring-purple-500/20"
                />
                <button onClick={() => setSelectedClass(null)} className="px-6 py-3 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-xl font-bold text-xs hover:bg-gray-200 transition-all flex items-center gap-2">
                    <i className="fas fa-arrow-left"></i> Back
                </button>
              </>
          )}
        </div>
      </div>

      {!selectedClass ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {classes.map(c => (
            <div 
              key={c.id} 
              onClick={() => setSelectedClass(c)}
              className="bg-white dark:bg-dark-card p-8 rounded-[2.5rem] border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-2xl hover:-translate-y-2 cursor-pointer transition-all group overflow-hidden relative"
            >
              <div className="absolute top-0 right-0 p-6 opacity-5 group-hover:scale-110 transition-transform">
                <i className="fas fa-users-viewfinder text-6xl"></i>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-purple-600 flex items-center justify-center text-white shadow-xl mb-6">
                <i className="fas fa-school text-xl"></i>
              </div>
              <h3 className="text-xl font-black dark:text-white mb-2">{c.name}</h3>
              <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-widest">
                <i className="fas fa-user-graduate text-purple-500"></i>
                Room Enrollment
              </div>
              <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-50 dark:border-gray-800">
                <span className="text-[10px] font-black text-purple-600 uppercase tracking-widest">Open Register</span>
                <i className="fas fa-chevron-right text-xs text-purple-600 group-hover:translate-x-1 transition-transform"></i>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white dark:bg-dark-card p-6 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-blue-500 flex items-center justify-center text-white shadow-lg"><i className="fas fa-users"></i></div>
                    <div><p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Enrolled Students</p><h4 className="text-xl font-black dark:text-white">{classStudents.length}</h4></div>
                </div>
                <div className="bg-white dark:bg-dark-card p-6 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-green-500 flex items-center justify-center text-white shadow-lg"><i className="fas fa-check"></i></div>
                    <div><p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Present</p><h4 className="text-xl font-black dark:text-white">{attendanceLogs.length}</h4></div>
                </div>
                <div className="bg-white dark:bg-dark-card p-6 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-sm flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-red-500 flex items-center justify-center text-white shadow-lg"><i className="fas fa-times"></i></div>
                    <div><p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Absent/Pending</p><h4 className="text-xl font-black dark:text-white">{classStudents.length - attendanceLogs.length}</h4></div>
                </div>
            </div>

            <div className="bg-white dark:bg-dark-card rounded-[2.5rem] border border-gray-100 dark:border-gray-800 shadow-xl overflow-hidden">
                <div className="p-6 bg-gray-50/50 dark:bg-gray-800/30 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center">
                    <h3 className="text-xs font-black dark:text-white uppercase tracking-widest">Daily Log: {new Date(viewDate).toLocaleDateString(undefined, { dateStyle: 'long' })}</h3>
                </div>
                <div className="table-container">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50/30 dark:bg-gray-800/10">
                            <tr>
                                <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Student Profile</th>
                                <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Check-In Time</th>
                                <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Attendance Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                            {classStudents.map(student => {
                                const log = attendanceLogs.find(l => l.studentId === student.id);
                                const isPastDate = viewDate < todayStr;

                                return (
                                    <tr key={student.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors group">
                                        <td className="px-8 py-5">
                                            <div className="flex items-center gap-4">
                                                <img src={student.photoURL} className="w-11 h-11 rounded-2xl object-cover ring-2 ring-transparent group-hover:ring-purple-500 transition-all" alt="" />
                                                <div>
                                                    <p className="font-black dark:text-white text-sm">{student.displayName}</p>
                                                    <p className="text-[10px] text-gray-400 font-bold uppercase">{student.email}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-5">
                                            {log ? (
                                                <span className="text-xs font-bold text-gray-500 font-mono">
                                                    {new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                            ) : (
                                                <span className="text-xs text-gray-300 italic font-medium">---</span>
                                            )}
                                        </td>
                                        <td className="px-8 py-5 text-right">
                                            {log ? (
                                                <span className="px-4 py-1.5 bg-green-500/10 text-green-600 text-[10px] font-black rounded-full uppercase tracking-widest border border-green-200 dark:border-green-900/30 shadow-sm">
                                                    <i className="fas fa-check-circle mr-1.5"></i> Present
                                                </span>
                                            ) : isPastDate ? (
                                                <span className="px-4 py-1.5 bg-red-500/10 text-red-600 text-[10px] font-black rounded-full uppercase tracking-widest border border-red-200 dark:border-red-900/30 shadow-sm">
                                                    <i className="fas fa-times-circle mr-1.5"></i> Absent
                                                </span>
                                            ) : (
                                                <span className="px-4 py-1.5 bg-orange-500/10 text-orange-600 text-[10px] font-black rounded-full uppercase tracking-widest border border-orange-200 dark:border-orange-900/30 shadow-sm">
                                                    <i className="fas fa-clock mr-1.5"></i> Pending
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceManagement;
